import React from 'react'
import PropTypes from 'prop-types'
import { View } from 'components/View'
import ContentBox from 'components/ContentBox'
import TextTitleCompressedHeavy from 'components/TextTitleCompressedHeavy'
import TextBodySmaller from 'components/TextBodySmaller'
import TextBody from 'components/TextBody'
import Video from 'components/Video'
import ScoreCard from 'modules/exercise-form-check/components/ScoreCard'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import { useTheme } from 'modules/styles'

const messages = defineMessages({
  overallScore: {
    id: 'ResultsSection.overallScore',
    defaultMessage: 'Overall Score'
  },
  control: {
    id: 'ResultsSection.control',
    defaultMessage: 'Control'
  },
  rangeOfMotion: {
    id: 'ResultsSection.rangeOfMotion',
    defaultMessage: 'Range of Motion'
  },
  tempo: {
    id: 'ResultsSection.tempo',
    defaultMessage: 'Tempo'
  },
  alignment: {
    id: 'ResultsSection.alignment',
    defaultMessage: 'Alignment'
  },
  symmetry: {
    id: 'ResultsSection.symmetry',
    defaultMessage: 'Symmetry'
  },
  disclaimer: {
    id: 'ResultsSection.disclaimer',
    defaultMessage: 'Results may not be 100% accurate'
  }
})

export const ResultsSection = ({
  formCheckResult,
  onScoreCardClick
}) => {
  const intl = useIntl()
  const theme = useTheme()

  if (!formCheckResult) {
    return null
  }

  return (
    <ContentBox mb={4}>
      {/* Video with results */}
      {formCheckResult.media?.url && (
        <View
          mb={3}
          style={{
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <Video
            source={formCheckResult.media.url}
            poster={formCheckResult.media.thumbnail}
            key={formCheckResult.media.url}
            width='100%'
          />
        </View>
      )}

      {/* Exercise Name */}
      <View mb={2}>
        <TextTitleCompressedHeavy>
          {formCheckResult.exerciseName || 'Exercise Form Check'}
        </TextTitleCompressedHeavy>
      </View>

      {/* Summary */}
      <View mb={4}>
        <TextBody style={{ color: theme.colors.paragraph }}>
          {formCheckResult.summary}
        </TextBody>
      </View>

      {/* Score Cards Grid */}
      <View
        mb={3}
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '1em'
        }}
      >
        <ScoreCard
          emoji='🎯'
          label={formatMessageIntlUtil(intl, messages.overallScore)}
          score={formCheckResult.overallScore}
          onClick={() => onScoreCardClick('overallScore')}
        />
        <ScoreCard
          emoji='⚖️'
          label={formatMessageIntlUtil(intl, messages.control)}
          score={formCheckResult.controlScore}
          onClick={() => onScoreCardClick('control')}
        />
        <ScoreCard
          emoji='📏'
          label={formatMessageIntlUtil(intl, messages.rangeOfMotion)}
          score={formCheckResult.rangeScore}
          onClick={() => onScoreCardClick('range')}
        />
        <ScoreCard
          emoji='⏱️'
          label={formatMessageIntlUtil(intl, messages.tempo)}
          score={formCheckResult.tempoScore}
          onClick={() => onScoreCardClick('tempo')}
        />
        <ScoreCard
          emoji='📐'
          label={formatMessageIntlUtil(intl, messages.alignment)}
          score={formCheckResult.alignmentScore}
          onClick={() => onScoreCardClick('alignment')}
        />
        <ScoreCard
          emoji='↔️'
          label={formatMessageIntlUtil(intl, messages.symmetry)}
          score={formCheckResult.symmetryScore}
          onClick={() => onScoreCardClick('symmetry')}
        />
      </View>

      {/* Disclaimer */}
      <View style={{ textAlign: 'center' }}>
        <TextBodySmaller style={{
          color: theme.colors.paragraph,
          fontStyle: 'italic'
        }}
        >
          {formatMessageIntlUtil(intl, messages.disclaimer)}
        </TextBodySmaller>
      </View>
    </ContentBox>
  )
}

ResultsSection.propTypes = {
  formCheckResult: PropTypes.object,
  onScoreCardClick: PropTypes.func.isRequired
}
